import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { captureUtmFromUrl } from "@/lib/utm";

/**
 * GATracker — fires a GA4 `page_view` on every client-side route change
 * AND captures UTM / gclid params on the first landing so we can attribute
 * every downstream form submission back to its ad campaign.
 *
 * gtag.js in `public/index.html` handles the initial page_view on load.
 * React Router does NOT trigger a browser navigation on route changes, so
 * subsequent /confirmation, /blog/:slug, /audit etc. would otherwise be
 * invisible in GA. This tiny component fixes that.
 *
 * Skipped for the first render (initial page_view already fired by gtag)
 * so we never double-count the landing page.
 */
export default function GATracker() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Attribution capture is always safe — first-touch wins so it's idempotent
    captureUtmFromUrl();

    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    // gtag.js has already fired page_view for the initial URL. Only send
    // additional page_view events when the SPA changes routes.
    if (window.__ga_last_path === undefined) {
      window.__ga_last_path = pathname + search;
      return;
    }
    if (window.__ga_last_path === pathname + search) return;
    window.__ga_last_path = pathname + search;

    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}
